import { errorToast } from './ToastService';
import HttpService from './HttpService';
import { AuthStore } from '../stores/auth';
import type { IUser } from '@/schemas/Users';

/**
 * Set the site title
 * @param title string
 */
export function setSiteTitle(title: string) {
  document.title = title;
}

export const sleepNow = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));
export const GLOBAL_MIN_AGE_YEARS = 13;

export function getFormattedDate(date: string) {
  try {
    // Format the date time for available events/challenges
    return new Date(date).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZoneName: 'short',
    });
  } catch (error: any) {
    errorToast(error.message);
  }
}

export function darkModeToggle() {
  const html = document.querySelector('html');
  if (html) {
    html.classList.toggle('unify-dark-mode');
    html.classList.toggle('dark'); // Added for Tailwind compatibility
  }
}

export async function loadDevJwtFromApi(): Promise<void> {
  if (!import.meta.env.DEV || !import.meta.env.VITE_LOCAL_DEV_EMAIL) {
    return;
  }

  const authStore = AuthStore();
  if (authStore.getIsLoggedIn) {
    return;
  }

  const resp = await HttpService.post('/auth/dev-login', {
    email: import.meta.env.VITE_LOCAL_DEV_EMAIL as string,
  });
  const { accessToken, refreshToken, user } = resp.data;
  authStore.setTokens(accessToken, refreshToken);
  authStore.setUser({
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    zipCode: '',
  } as IUser);
  console.log('DEV: Auto-logged in as', user.email);
}

export function loadDevSessionFromEnv() {
  if (!import.meta.env.DEV || !import.meta.env.VITE_LOCAL_USER_SESSION) {
    return;
  }

  try {
    const LOCAL_USER_SESSION = JSON.parse((import.meta.env.VITE_LOCAL_USER_SESSION as string).replace(/'/g, '"'));

    // If LOCAL_USER_SESSION is defined and the sessionid and csrftoken cookies are not already set, set them
    if (LOCAL_USER_SESSION && LOCAL_USER_SESSION.sessionid && LOCAL_USER_SESSION.csrftoken && !document.cookie.includes('sessionid=') && !document.cookie.includes('csrftoken=')) {
      console.log('DEV: Setting session and csrf for local user.');

      // Set the cookies for local user session
      document.cookie = `sessionid=${LOCAL_USER_SESSION.sessionid}; path=/; SameSite=Lax`;
      document.cookie = `csrftoken=${LOCAL_USER_SESSION.csrftoken}; path=/; SameSite=Lax`;
    }
  } catch (error) {
    throw new Error('Failed to parse VITE_LOCAL_USER_SESSION. Please ensure it is valid JSON.');
  }
}

function parseDobString(dobString: string): Date | null {
  const parts = dobString.split('/');
  let month: number;
  let day: number;
  let year: number;

  if (parts.length === 3) {
    month = parseInt(parts[0], 10) - 1;
    day = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  } else if (parts.length === 2) {
    month = parseInt(parts[0], 10) - 1;
    day = 1;
    year = parseInt(parts[1], 10);
  } else {
    return null;
  }

  if (isNaN(month) || isNaN(day) || isNaN(year)) {
    return null;
  }

  return new Date(year, month, day);
}

// The latest date of birth that is at least minimumAge years ago today. Today's day is capped at the length
// of the target month, so on Feb 29 a non-leap target year gives Feb 28 instead of rolling over to Mar 1
// (which would let someone born on Mar 1 through a day before they reach the age).
export function getMinimumAgeThresholdDate(minimumAge = GLOBAL_MIN_AGE_YEARS): Date {
  const today = new Date();
  const year = today.getFullYear() - minimumAge;
  const daysInTargetMonth = new Date(year, today.getMonth() + 1, 0).getDate();
  return new Date(year, today.getMonth(), Math.min(today.getDate(), daysInTargetMonth));
}

export function isOverMinimumAge(dob: Date, minimumAge = GLOBAL_MIN_AGE_YEARS): boolean {
  if (!dob) return false;

  return dob <= getMinimumAgeThresholdDate(minimumAge);
}

// Calculate age from date of birth string (supports MM/DD/YYYY and MM/YYYY formats)
export function calculateAge(dobString: string): number {
  if (!dobString) return 0;

  const dob = parseDobString(dobString);
  if (!dob) {
    console.warn('Invalid date format:', dobString);
    return 0;
  }

  const today = new Date();

  if (dob > today) {
    return 0;
  }

  let calculatedAge = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    calculatedAge--;
  }

  return Math.max(0, calculatedAge);
}